import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import Accordion from '../../../../../components/Accordion';
import BasicButton from '../../../../../components/BasicButton';
import BasicTitle from '../../../../../components/BasicTitle';
import FormRadio from '../../../../../components/FormRadio';
import FormCheckbox from '../../../../../components/FormCheckbox';
import LinkButton from '../../../../../components/LinkButton';
import {ACCOUNT_UNSUBSCRIBE_MODE} from '../../../../../constants/account';
import POPUP_STYLES from '../../../../../constants/popup-styles';
import AccountUnsubscribeConfirmDialog from '../../../../Dialog/components/AccountUnsubscribeConfirmDialog';
import BasicDialog from '../../../../Dialog/components/BasicDialog';
import PauseEmailsConfirmDialog from '../../../../Dialog/components/PauseEmailsConfirmDialog';
import PauseEmailsDialog from '../../../../Dialog/components/PauseEmailsDialog';
import './CandidateNotificationsSettings.styles.css';

const RECOMMENDED_SETTINGS_FIELDS = [
  {
    label: 'Actively Recruiting',
    value: 'activelyRecruiting',
    helpText: 'Keep on top of jobs',
  },
  {
    label: 'Keeping An Eye Out',
    value: 'keepingAnEyeOut',
    helpText: 'Receive weekly summaries of positions that match your interests',
  },
  {
    label: 'Off The Market',
    value: 'offTheMarket',
    helpText: 'Pause job notifications for 6 months',
  },
];

const JOB_UPDATES_FIELDS = [
  {
    label: 'Real Time Email',
    value: 'realTimeDigest',
    helpText: 'Real-time emails when jobs match your interests',
  },
  {
    label: 'Daily Digest',
    value: 'dailyDigest',
    helpText: 'Receive matching jobs via daily email',
  },
  {
    label: 'Weekly Digest',
    value: 'weeklyDigest',
    helpText: 'Receive matching jobs via weekly email',
  },
];

const PRESETS = {
  activelyRecruiting: {
    jobUpdates: 'realTimeDigest',
    IsSubscribed: 'Yes',
    ReceiveMessages: 'Yes',
    PlatformActivity: 'Yes',
    MonthlyNewsLetter: 'Yes',
    Unsubscribe: 'No',
  },
  keepingAnEyeOut: {
    jobUpdates: 'weeklyDigest',
    IsSubscribed: 'Yes',
    ReceiveMessages: 'Yes',
    PlatformActivity: 'Yes',
    MonthlyNewsLetter: 'Yes',
    Unsubscribe: 'No',
  },
  offTheMarket: {
    Unsubscribe: 'OffMarket',
    jobUpdates: null,
    IsSubscribed: 'No',
  },
  custom: {
    Unsubscribe: 'No',
  }
};

const CandidateNotificationsSettings = ({settings, params, onChange}) => {
  const initialized = useRef(false);
  const previousRecommendedSettings = useRef(null);
  const [state, setState] = React.useState({});
  const unsubscribeExpireDate = useMemo(() => {
    if (!settings || !params) {
      return null;
    }

    const { UnsubscribeExpireDate, UnsubscribeExpireDefaultDate } = params
    const expireDate = !UnsubscribeExpireDate
      ? UnsubscribeExpireDefaultDate
      : UnsubscribeExpireDate
    return (settings.Unsubscribe === ACCOUNT_UNSUBSCRIBE_MODE.SOFT || settings.Unsubscribe === ACCOUNT_UNSUBSCRIBE_MODE.OFF_MARKET) ? `Paused until ${expireDate}` : null
  }, [params, settings]);

  const recommendedSettingsFields = useMemo(() => {
    const settings = [
      ...RECOMMENDED_SETTINGS_FIELDS,
    ];

    if (state.EmailRecommendedSettings === 'custom') {
      settings.push({
        label: 'Custom Settings',
        value: 'custom',
        helpText: 'Adjust the settings manually',
      });
    }

    return settings;
  }, [state.EmailRecommendedSettings]);

  const getJobUpdatesValue = useCallback((value) => {
    const getRealTimeDigestValue = (checked) => {
      if (checked && state.privacy && state.privacy.includes('7')) {
        return state.privacy.filter(item => item !== '7');
      }

      if (!checked && state.privacy && !state.privacy.includes('7')) {
        return [...state.privacy, '7'];
      }

      return state.privacy;
    };

    return {
      DailyDigest: value === 'dailyDigest' ? 'Yes' : 'No',
      WeeklyDigest: value === 'weeklyDigest' ? 'Yes' : 'No',
      privacy: getRealTimeDigestValue(value === 'realTimeDigest'),
    }
  }, [state.privacy]);

  const jobUpdatesValue = useMemo(() => {
    if (state.DailyDigest === 'Yes') {
      return 'dailyDigest';
    }

    if (state.WeeklyDigest === 'Yes') {
      return 'weeklyDigest';
    }

    if (state.privacy && !state.privacy.includes('7')) {
      return 'realTimeDigest';
    }

    return null;
  }, [state.privacy, state.DailyDigest, state.WeeklyDigest]);

  const handleRecommendedSettingsChange = useCallback((e) => {
    const value = e.target.value;

    if (PRESETS[value]) {
      const {jobUpdates, ...preset} = PRESETS[value];
      const jobUpdatesValue = getJobUpdatesValue(jobUpdates);

      setState({
        ...state,
        ...preset,
        ...jobUpdatesValue,
        EmailRecommendedSettings: value,
      });
      return;
    }

    setState({
      ...state,
      EmailRecommendedSettings: value,
    })
  }, [state]);

  const handleJobUpdatesChange = useCallback((e) => {
    const value = e.target.value;

    setState({
      ...state,
      ...getJobUpdatesValue(value),
      EmailRecommendedSettings: 'custom',
      Unsubscribe: 'No',
    });
  }, [state]);

  const unsubscribe = useCallback((mode, duration = 6) => {
    const newState = {
      ...state,
      ...getJobUpdatesValue(null),
      EmailRecommendedSettings: null,
      ReceiveMessages: 'No',
      PlatformActivity: 'No',
      IsSubscribed: 'No',
      Unsubscribe: mode,
      PauseDuration: duration,
    };

    if (mode === ACCOUNT_UNSUBSCRIBE_MODE.HARD) {
      newState.MonthlyNewsLetter = 'No';
    }

    setState(newState);
  }, [state]);

  useEffect(() => {
    if (settings) {
      setState({
        ...settings,
      });
      initialized.current = true;
    }
  }, [settings]);

  useEffect(() => {
    if (initialized.current) {
      onChange(state);
    }
  }, [state]);

  useEffect(() => {
    if (previousRecommendedSettings.current === 'offTheMarket' && state.EmailRecommendedSettings === 'custom') {
      BasicDialog({
        bodyRender: 'Setting updated. All active notifications have been unpaused.',
        bodyStyles: POPUP_STYLES.warningBodyStyles,
        popupStyles: POPUP_STYLES.warningPopupStyles,
        popupInnerStyles: POPUP_STYLES.warningContentStyles,
        withoutButtons: false,
      });
    }

    return () => {
      previousRecommendedSettings.current = state.EmailRecommendedSettings;
    }
  }, [state.EmailRecommendedSettings]);

  return (
    <div className={'form__content form__content--full-width'}>
      <div className="form__header"><a name="candidate-notifications"></a>
        <h4 className="form__title">Candidate notifications</h4>
      </div>

      <div className={'form__group candidate-notifications'}>
        <Accordion
          label={'Main Settings'}
          isOpen={true}
        >
          <FormRadio
            name={'recommendedSettings'}
            options={recommendedSettingsFields}
            value={state.EmailRecommendedSettings}
            onChange={handleRecommendedSettingsChange}
            mark={state.EmailRecommendedSettings === 'offTheMarket' && unsubscribeExpireDate}
            wide={true}
          />
        </Accordion>
        <Accordion
          label={'Detailed Settings'}
          footerBlocks={[

          ]}
        >
          <BasicTitle
            text="Job Updates"
            fontSize={12}
            styles={{marginBottom: '16px', marginTop: '5px'}}
          />
          <FormRadio
            name={'jobUpdates'}
            options={JOB_UPDATES_FIELDS}
            value={jobUpdatesValue}
            onChange={handleJobUpdatesChange}
            mark={jobUpdatesValue === null && unsubscribeExpireDate}
            error={!jobUpdatesValue && 'All job notifications paused. Recommend enabling weekly digest.'}
            wide={true}
          />
          <FormCheckbox
            name={'IsSubscribed'}
            label={'Direct Job Invites'}
            helpText={'Manual outreach from GoBuyside team on select high fit jobs (recommended)'}
            checked={state.IsSubscribed === 'Yes'}
            mark={state.IsSubscribed !== 'Yes' && unsubscribeExpireDate}
            error={state.IsSubscribed === 'No' && 'Recommend enabling this for the best platform experience.'}
            onChange={() => {
              setState({
                ...state,
                IsSubscribed: state.IsSubscribed === 'Yes' ? 'No' : 'Yes',
                EmailRecommendedSettings: 'custom',
                Unsubscribe: 'No',
              })
            }}
          />
          <hr/>
          <BasicTitle
            text="Other Updates"
            fontSize={12}
            styles={{marginBottom: '16px', marginTop: '5px'}}
          />
          <FormCheckbox
            name={'ReceiveMessages'}
            label={'Platform Messages'}
            helpText={'Get an email when you receive a message in the platform (recommended)'}
            checked={state.ReceiveMessages === 'Yes'}
            mark={state.ReceiveMessages !== 'Yes' && unsubscribeExpireDate}
            onChange={() => {
              setState({
                ...state,
                ReceiveMessages: state.ReceiveMessages === 'Yes' ? 'No' : 'Yes',
                EmailRecommendedSettings: 'custom',
                Unsubscribe: 'No',
              })
            }}
          />
          <FormCheckbox
            name={'PlatformActivity'}
            label={'Platform Activity'}
            helpText={'Receive email updates driven by platform activity (connection requests, relevant reviews, etc.)'}
            checked={state.PlatformActivity === 'Yes'}
            mark={state.PlatformActivity !== 'Yes' && unsubscribeExpireDate}
            onChange={() => {
              setState({
                ...state,
                PlatformActivity: state.PlatformActivity === 'Yes' ? 'No' : 'Yes',
                EmailRecommendedSettings: 'custom',
                Unsubscribe: 'No',
              })
            }}
          />
          <FormCheckbox
            name={'MonthlyNewsLetter'}
            label={'Newsletter'}
            helpText={'Receive our monthly newsletter (Recommended)'}
            checked={state.MonthlyNewsLetter === 'Yes'}
            error={state.MonthlyNewsLetter === 'No' && 'Recommend enabling this to stay informed with occasional updates.'}
            onChange={() => {
              setState({
                ...state,
                MonthlyNewsLetter: state.MonthlyNewsLetter === 'Yes' ? 'No' : 'Yes',
                EmailRecommendedSettings: 'custom',
                Unsubscribe: 'No',
              })
            }}
          />
          <hr/>
          <BasicTitle
            text="Pause or Unsubscribe"
            fontSize={12}
            styles={{marginBottom: '5px'}}
          />
          <div className="form__row form__row--footer">
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '5px'}}>
              <BasicButton
                btnFontWeight={400}
                btnTextColor={"#27282d"}
                btnArrow={false}
                controlFunc={() => {
                  PauseEmailsDialog().then((option) => {
                    if (option.value === 'tooManyEmails') {
                      PauseEmailsConfirmDialog(option).then((value) => {
                        if (value === 'keepWeeklyDigest') {
                          const {jobUpdates, ...preset} = PRESETS['custom'];
                          const jobUpdatesValue = getJobUpdatesValue('weeklyDigest');

                          setState({
                            ...state,
                            ...preset,
                            ...jobUpdatesValue,
                            PlatformActivity: 'No',
                            EmailRecommendedSettings: 'custom',
                          });
                        } else {
                          unsubscribe(ACCOUNT_UNSUBSCRIBE_MODE.SOFT);
                        }
                      });
                    } else {
                      unsubscribe(ACCOUNT_UNSUBSCRIBE_MODE.SOFT, option.pauseDuration);
                      BasicDialog({
                        bodyRender: option.message,
                        bodyStyles: POPUP_STYLES.warningBodyStyles,
                        popupStyles: POPUP_STYLES.warningPopupStyles,
                        popupInnerStyles: POPUP_STYLES.warningContentStyles,
                        withoutButtons: false,
                      });
                    }
                  });
                }}
                btnText={'Unsubscribe/Pause'}
              />
              <p>
                Click above to stop job notifications when you are not recruiting. You can also stop all emails by&nbsp;
                <LinkButton
                  btnFontWeight={400}
                  btnTextColor={"#27282d"}
                  controlFunc={() => {
                    AccountUnsubscribeConfirmDialog()
                      .then((isRecommended) => {
                        const mode = isRecommended
                          ? ACCOUNT_UNSUBSCRIBE_MODE.SOFT
                          : ACCOUNT_UNSUBSCRIBE_MODE.HARD

                        unsubscribe(mode);
                      })
                  }}
                  text={'Unsubscribing All'}
                />
                &nbsp;here.
              </p>
            </div>
          </div>
        </Accordion>
      </div>
    </div>
  )
};

export default CandidateNotificationsSettings;
